import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  markAbTestAsError,
  markAbTestAsLoaded,
  markAbTestAsLoading,
  markAbTestAsSkipped,
} from '@bridebook/toolbox/src/ab-testing';
import { participatedInAbTestingAnalytics } from '@bridebook/toolbox/src/ab-testing/lib/actions/actions';
import { getExperimentCohort } from '@bridebook/toolbox/src/ab-testing/lib/get-experiment-cohort';
import { getIsE2E } from '@bridebook/toolbox/src/e2e/get-is-e2e';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { useAbTestsFetch } from './use-ab-tests-fetch';
import { AbTestingState, getAbTestLifecycleState } from '../selectors';
import type { CohortNames } from '../types';

const TIMEOUT_MS = 25_000;

interface UseABTestProps {
  triggerCondition?: boolean;
  userId: string | undefined;
  testId: string;
}

interface UseABTestResult {
  variant: CohortNames | null;
  isInitialized: boolean;
}

/**
 * Enhanced AB test hook that tracks per-test loading state.
 *
 * Unlike `useActivateAbTest`, this hook immediately initializes the test
 * in the Redux store with a "loading" status, so consumers always know
 * whether a test is still loading vs. resolved.
 *
 * Resolution scenarios:
 * - Test found in CMS and activated -> status: 'loaded', assigned variant
 * - Test not found in CMS (intentional - removed / inactive test) -> status: 'loaded', variant: 'control'
 * - API returned empty results -> status: 'error', variant: 'control'
 * - API threw an API Error -> status: 'error', variant: 'control'
 * - Loading exceeded 25s timeout -> status: 'error', variant: 'control'
 * - A/B test's triggerCondition is initially false -> status: 'skipped', variant: 'control'
 * @param triggerCondition (optional)
 * - undefined - trigger is not ready
 * - false - shouldn't be triggered
 * - true - should be triggered
 * @param userId - user id
 * @param testId - id of the test from firestore
 * @returns { variant, isInitialized } where isInitialized is false only while loading
 */
export const useABTest = ({
  triggerCondition,
  userId,
  testId,
}: UseABTestProps): UseABTestResult => {
  const dispatch = useDispatch();
  const { abTests, abTestsLoaded, error } = useAbTestsFetch();
  const abTest = abTests?.find((test) => test.id === testId);
  const lifecycleState = useSelector((state: AbTestingState) =>
    getAbTestLifecycleState(state, testId),
  );
  const status = lifecycleState?.status;

  useEffect(() => {
    if (!status) {
      dispatch(markAbTestAsLoading(testId));
    }
  }, [status, dispatch, testId]);

  useEffect(() => {
    if (triggerCondition === undefined) return;

    if (triggerCondition && status === 'skipped') {
      dispatch(markAbTestAsLoading(testId));
    }
    if (!triggerCondition && status === 'loading') {
      dispatch(markAbTestAsSkipped(testId));
    }
  }, [status, triggerCondition, dispatch, testId]);

  useEffect(() => {
    const isReadyToResolve =
      status === 'loading' && abTestsLoaded && triggerCondition && userId && !getIsE2E();

    if (!isReadyToResolve) return;

    if (error || !abTests?.length) {
      SentryMinimal().captureException(error ?? `[useABTest] Empty tests`, {
        tags: { abTestId: testId },
      });
      dispatch(markAbTestAsError(testId));
      return;
    }

    if (!abTest) {
      // eslint-disable-next-line no-console
      console.warn(`[useABTest] Missing test: ${testId}`, {
        tags: { abTestId: testId },
      });
      dispatch(markAbTestAsLoaded({ id: testId, variant: 'control' }));
      return;
    }

    const variant = getExperimentCohort(abTest, userId);
    dispatch(markAbTestAsLoaded({ id: testId, variant }));
    dispatch(participatedInAbTestingAnalytics({ abTest, variant }));
  }, [abTestsLoaded, abTests, abTest, error, status, dispatch, testId, triggerCondition, userId]);

  useEffect(() => {
    const isWaitingForData =
      status === 'loading' && !abTestsLoaded && triggerCondition && userId && !getIsE2E();

    if (!isWaitingForData) return;

    const timer = setTimeout(() => {
      SentryMinimal().captureMessage(`[useABTest] Timed out after ${TIMEOUT_MS}ms for: ${testId}`, {
        tags: { abTestId: testId },
      });
      dispatch(markAbTestAsError(testId));
    }, TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [dispatch, testId, status, triggerCondition, userId, abTestsLoaded]);

  return {
    variant: lifecycleState?.variant ?? null,
    isInitialized: status === 'loaded' || status === 'error' || status === 'skipped',
  };
};
